import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, SelectField, TextField } from 'components/ui';
import { FieldShell, RequiredMark } from 'components/ui/FieldShell';
import {
  emailError,
  formatPhone,
  isValidEmail,
  isValidPhone,
  phoneError,
} from 'features/scla/utils/contactFields';
import updateSclaAppSchool from 'features/scla/api/updateSclaAppSchool';
import useSchoolList from 'features/schools/hooks/useSchoolList';
import { IScoolExtraCurricular, IUpdateSclaSchool } from 'features/scla/types';
import {
  getSclaAppFamilyPath,
  getSclaAppPromptsPath,
} from 'utils/navigation';
import ChipButton from '../components/steps/ChipButton';
import SclaStepCard from '../components/steps/SclaStepCard';
import styles from '../components/steps/SclaStep.module.scss';
import { SCLA_CAREER_FIELDS } from '../config/applicationOptions';
import useAutosave, { useSectionReady } from '../hooks/useAutosave';
import useSclaSectionPath from '../hooks/useSclaSectionPath';
import { SclaApplicationOutletContext } from './SclaAppLayout';
import clearFilledErrors from '../utils/clearFilledErrors';

const EMPTY_ACTIVITY: IScoolExtraCurricular = {
  name: '',
  position: '',
  hours: '',
  grade: '',
};

const ACTIVITY_GRADES = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map(label => ({
  label,
  value: label,
}));

function gpaInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  const whole = cleaned.slice(0, dot);
  const decimals = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return `${whole}.${decimals}`;
}

function gpaError(value: string, required = true): string {
  const gpa = value.trim();
  if (!gpa) return required ? 'Enter your GPA.' : '';
  if (!/^\d+(\.\d{1,2})?$/.test(gpa)) return 'Enter a GPA from 0.0 to 5.0.';
  const number = Number(gpa);
  if (number < 0 || number > 5) return 'Enter a GPA from 0.0 to 5.0.';
  return '';
}

function activityGrade(value: string): string {
  const match = value.trim().match(/^(\d{1,2})/);
  if (!match) return value.trim();
  const grade = Number(match[1]);
  if (grade >= 6 && grade <= 12) return `${grade}th`;
  return value.trim();
}

function growTextarea(event: React.FormEvent<HTMLTextAreaElement>) {
  const node = event.currentTarget;
  node.style.height = 'auto';
  node.style.height = `${node.scrollHeight}px`;
}

function reveal(id: string) {
  const node = document.getElementById(id);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if ('focus' in node) (node as HTMLElement).focus();
}

function text(value: string | null | undefined) {
  return value || '';
}

function schoolChoiceFrom(application: SclaApplicationOutletContext['application']) {
  const stored = text(application.schoolInfo.schoolName);
  if (stored.toLowerCase() === 'other') return 'other';
  return stored;
}

function mapSchool(application: SclaApplicationOutletContext['application']): IUpdateSclaSchool {
  const school = application.schoolInfo;
  return {
    schoolName: text(school.schoolName),
    counselorName: text(school.counselorName),
    counselorPhoneNumber: formatPhone(text(school.counselorPhoneNumber)),
    counselorEmail: text(school.counselorEmail),
    gpa: text(school.gpa),
    hobbies: text(school.hobbies),
    careerInterests: school.careerInterests || [],
    careerInterestsOther: text(school.careerInterestsOther),
    schoolOther: text(school.schoolOther),
    extraCurriculars: school.extraCurriculars?.length
      ? school.extraCurriculars.map(item => ({
          ...item,
          hours: (item.hours || '').replace(/\D/g, ''),
          grade: activityGrade(item.grade || ''),
        }))
      : [{ ...EMPTY_ACTIVITY }],
  };
}

function validSnapshot(school: IUpdateSclaSchool) {
  return {
    gpa: gpaError(school.gpa, true) ? '' : school.gpa.trim(),
    email: isValidEmail(school.counselorEmail) ? school.counselorEmail.trim() : '',
    phone: isValidPhone(school.counselorPhoneNumber)
      ? formatPhone(school.counselorPhoneNumber)
      : '',
  };
}

interface ISchoolErrors {
  school?: string;
  gpa?: string;
  counselorName?: string;
  counselorEmail?: string;
  counselorPhone?: string;
  counselorContact?: string;
  careers?: string;
  careerOther?: string;
  hobbies?: string;
}

export default function SclaApplicationSchool() {
  const { application, setApplication, readOnly } =
    useOutletContext<SclaApplicationOutletContext>();
  const { schoolOptions, knownSchoolValues, loading: schoolsLoading } = useSchoolList();
  const sectionPath = useSclaSectionPath();
  const navigate = useNavigate();
  const school = application.schoolInfo;
  const knownCareers = SCLA_CAREER_FIELDS.map(item => item.value);
  const [form, setForm] = useState<IUpdateSclaSchool>(() => mapSchool(application));
  const [schoolChoice, setSchoolChoice] = useState(() => schoolChoiceFrom(application));
  const [hydrated, setHydrated] = useState(false);
  const [errors, setErrors] = useState<ISchoolErrors>({});
  const reconciledSchool = useRef(false);
  const lastValid = useRef(validSnapshot(mapSchool(application)));
  const ready = useSectionReady(application._id, () => {
    const next = mapSchool(application);
    lastValid.current = validSnapshot(next);
    setForm(next);
    setSchoolChoice(schoolChoiceFrom(application));
    setErrors({});
    reconciledSchool.current = false;
    setHydrated(false);
  });

  const schoolChoices = useMemo(
    () => [...schoolOptions, { label: 'Other', value: 'other' }],
    [schoolOptions],
  );

  useEffect(() => {
    if (schoolsLoading || reconciledSchool.current) return;
    reconciledSchool.current = true;
    const stored = text(school.schoolName);
    const storedOther = text(school.schoolOther);
    if (!stored) {
      setHydrated(true);
      return;
    }
    if (stored.toLowerCase() === 'other' || !knownSchoolValues.has(stored.toLocaleLowerCase())) {
      setSchoolChoice('other');
      setForm(current => ({
        ...current,
        schoolOther: storedOther || (stored.toLowerCase() === 'other' ? '' : stored),
      }));
      setHydrated(true);
      return;
    }
    const match = schoolOptions.find(
      option => option.value.toLocaleLowerCase() === stored.toLocaleLowerCase(),
    );
    if (match) setSchoolChoice(match.value);
    setHydrated(true);
  }, [schoolsLoading, knownSchoolValues, schoolOptions, school.schoolName, school.schoolOther]);

  const onSchoolChange = useCallback((value: string | null) => {
    const next = value ?? '';
    setSchoolChoice(next);
    setForm(current => ({
      ...current,
      schoolName: next === 'other' ? '' : next,
      schoolOther: next === 'other' ? current.schoolOther : '',
    }));
  }, []);

  const onSchoolOtherChange = useCallback((value: string) => {
    setForm(current => ({ ...current, schoolOther: value, schoolName: value }));
  }, []);

  const patch = (partial: Partial<IUpdateSclaSchool>) => {
    setForm(current => ({ ...current, ...partial }));
    setErrors(current => clearFilledErrors(current, partial, { schoolName: 'school', schoolOther: 'school', careerInterests: 'careers', careerInterestsOther: 'careerOther', counselorEmail: 'counselorContact', counselorPhoneNumber: 'counselorContact' }));
  };

  const legacyCareers = useMemo(
    () => (form.careerInterests || []).filter(item => !knownCareers.includes(item) && item !== 'other'),
    [form.careerInterests, knownCareers],
  );

  const save = async () => {
    const selected = form.careerInterests.filter(item => knownCareers.includes(item) || item === 'other');
    const customSchool = schoolChoice === 'other';
    const schoolName = customSchool ? text(form.schoolOther).trim() : schoolChoice;
    const payload: IUpdateSclaSchool = {
      ...form,
      schoolName,
      schoolOther: customSchool ? schoolName : '',
      gpa: lastValid.current.gpa,
      counselorEmail: lastValid.current.email,
      counselorPhoneNumber: lastValid.current.phone,
      careerInterests: [...selected, ...legacyCareers],
      extraCurriculars: form.extraCurriculars.filter(item =>
        item.name || item.position || item.hours || item.grade,
      ),
    };
    const response = await updateSclaAppSchool(application._id, payload);
    setApplication(response);
    return true;
  };

  const { flush } = useAutosave({
    serialized: JSON.stringify({ ...form, schoolChoice }),
    enabled: !readOnly && ready && hydrated,
    save,
  });

  const toggleCareer = (value: string) => {
    setForm(current => {
      const selected = current.careerInterests.includes(value);
      const careerInterests = selected
        ? current.careerInterests.filter(item => item !== value)
        : [...current.careerInterests, value];
      return { ...current, careerInterests };
    });
    setErrors(current => ({ ...current, careers: '' }));
  };

  const updateActivity = (index: number, partial: Partial<IScoolExtraCurricular>) => {
    setForm(current => ({
      ...current,
      extraCurriculars: current.extraCurriculars.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...partial } : item,
      ),
    }));
  };

  const changeGpa = (value: string) => {
    const next = gpaInput(value);
    patch({ gpa: next });
    setErrors(current => ({ ...current, gpa: next ? gpaError(next, false) : '' }));
    if (!next.trim()) lastValid.current.gpa = '';
    else if (!gpaError(next, true)) lastValid.current.gpa = next.trim();
  };

  const changeCounselorEmail = (value: string) => {
    patch({ counselorEmail: value });
    setErrors(current => ({ ...current, counselorEmail: emailError(value, false) }));
    if (!value.trim()) lastValid.current.email = '';
    else if (isValidEmail(value)) lastValid.current.email = value.trim();
  };

  const changeCounselorPhone = (value: string) => {
    const next = formatPhone(value);
    patch({ counselorPhoneNumber: next });
    setErrors(current => ({ ...current, counselorPhone: phoneError(next, false) }));
    if (!next.trim()) lastValid.current.phone = '';
    else if (isValidPhone(next)) lastValid.current.phone = next;
  };

  const handleContinue = async () => {
    const posted = await flush();
    const schoolName = schoolChoice === 'other' ? text(form.schoolOther).trim() : schoolChoice;
    const nextErrors: ISchoolErrors = {};
    if (!schoolName) nextErrors.school = 'Enter your school.';
    const gpaMessage = gpaError(form.gpa, true);
    if (gpaMessage) nextErrors.gpa = gpaMessage;
    if (!form.counselorName.trim()) nextErrors.counselorName = 'Enter a counselor or teacher name.';
    const mailMessage = emailError(form.counselorEmail, false);
    const phoneMessage = phoneError(form.counselorPhoneNumber, false);
    if (mailMessage) nextErrors.counselorEmail = mailMessage;
    if (phoneMessage) nextErrors.counselorPhone = phoneMessage;
    if (!form.counselorEmail.trim() && !form.counselorPhoneNumber.trim()) {
      nextErrors.counselorContact = 'Give at least an email or a phone.';
    }
    if (!form.careerInterests.some(item => item.trim())) {
      nextErrors.careers = 'Choose at least one career field.';
    }
    if (form.careerInterests.includes('other') && !form.careerInterestsOther.trim()) {
      nextErrors.careerOther = 'Enter the other career field.';
    }
    if (!form.hobbies.trim()) nextErrors.hobbies = 'Enter your hobbies and interests.';
    setErrors(nextErrors);
    const schoolField = schoolChoice === 'other' ? 'school-other' : 'school-name';
    const firstId = [
      nextErrors.school ? schoolField : '',
      nextErrors.gpa ? 'school-gpa' : '',
      nextErrors.counselorName ? 'counselor-name' : '',
      nextErrors.counselorEmail ? 'counselor-email' : '',
      nextErrors.counselorPhone ? 'counselor-phone' : '',
      nextErrors.counselorContact ? 'counselor-email' : '',
      nextErrors.careers ? 'scla-careers' : '',
      nextErrors.careerOther ? 'career-other' : '',
      nextErrors.hobbies ? 'hobbies' : '',
    ].find(id => id);
    if (firstId) reveal(firstId);
    const ok = posted && !firstId;
    if (ok) navigate(sectionPath(getSclaAppPromptsPath()));
    return ok;
  };

  return (
    <SclaStepCard
      title="School"
      intro="Your school, an adult there we can contact, and what you're into."
      backTo={sectionPath(getSclaAppFamilyPath())}
      onContinue={handleContinue}
      continueLabel="Save and continue →"
      readOnly={readOnly}
    >
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>School</legend>
        <div className={styles.grid}>
          <div className={styles.span}>
            <SelectField
              id="school-name"
              label="School"
              required
              value={schoolChoice || null}
              options={schoolChoices}
              onChange={onSchoolChange}
              disabled={readOnly}
              searchable
              placeholder="Search schools"
              error={schoolChoice === 'other' ? undefined : errors.school}
            />
            {schoolChoice === 'other' ? (
              <TextField
                id="school-other"
                label="School name"
                required
                value={text(form.schoolOther)}
                onChange={onSchoolOtherChange}
                error={errors.school}
              />
            ) : null}
          </div>
          <div>
            <TextField
              id="school-gpa"
              label="GPA (cumulative)"
              required
              value={form.gpa}
              onChange={changeGpa}
              error={errors.gpa}
            />
          </div>
        </div>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Counselor or teacher</legend>
        <div className={styles.grid}>
          <div className={styles.span}>
            <TextField
              id="counselor-name"
              label="Name"
              required
              value={form.counselorName}
              onChange={value => patch({ counselorName: value })}
              error={errors.counselorName}
            />
          </div>
          <TextField
            id="counselor-email"
            label="Email"
            type="email"
            value={form.counselorEmail}
            onChange={changeCounselorEmail}
            error={errors.counselorEmail}
          />
          <TextField
            id="counselor-phone"
            label="Phone"
            type="tel"
            value={form.counselorPhoneNumber}
            onChange={changeCounselorPhone}
            error={errors.counselorPhone}
          />
        </div>
        {errors.counselorContact && !errors.counselorEmail && !errors.counselorPhone ? (
          <p id="counselor-contact" className={styles.fieldError} role="alert">
            {errors.counselorContact}
          </p>
        ) : null}
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Your interests</legend>
        <div className={styles.control}>
        <p id="scla-careers" className={styles.question} tabIndex={-1}>
          Career fields you are interested in
          <RequiredMark />
        </p>
        <div className={styles.chipBlock}>
          {SCLA_CAREER_FIELDS.map(option => {
            const selected = form.careerInterests.includes(option.value);
            return (
              <ChipButton
                key={option.value}
                pressed={selected}
                className={selected ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                onClick={() => toggleCareer(option.value)}
              >
                {option.label}
              </ChipButton>
            );
          })}
        </div>
        {errors.careers ? <p className={styles.fieldError}>{errors.careers}</p> : null}
        {legacyCareers.length ? (
          <p className={styles.legacy}>{`Previous answer: ${legacyCareers.join(', ')}`}</p>
        ) : null}
        </div>
        {form.careerInterests.includes('other') ? (
          <TextField
            id="career-other"
            label="Other career field"
            required
            value={form.careerInterestsOther}
            onChange={value => patch({ careerInterestsOther: value })}
            error={errors.careerOther}
          />
        ) : null}
        <FieldShell id="hobbies" label="Hobbies and interests" required error={errors.hobbies}>
          <textarea
            id="hobbies"
            rows={3}
            className={errors.hobbies ? `${styles.growing} ${styles.growingError}` : styles.growing}
            value={form.hobbies}
            disabled={readOnly}
            aria-invalid={errors.hobbies ? true : undefined}
            aria-describedby={errors.hobbies ? 'hobbies-error' : undefined}
            onInput={growTextarea}
            onChange={event => patch({ hobbies: event.target.value })}
          />
        </FieldShell>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Activities and work</legend>
        {form.extraCurriculars.map((activity, index) => (
          <div className={styles.activity} key={`activity-${index}`}>
            <div className={styles.activityHead}>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Remove activity ${index + 1}`}
                onClick={() =>
                  setForm(current => ({
                    ...current,
                    extraCurriculars: current.extraCurriculars.filter(
                      (_, itemIndex) => itemIndex !== index,
                    ),
                  }))
                }
              >
                Remove
              </Button>
            </div>
            <div className={styles.grid}>
              <TextField
                id={`activity-name-${index}`}
                label="Activity, club, sport or job"
                value={activity.name || ''}
                onChange={value => updateActivity(index, { name: value })}
              />
              <TextField
                id={`activity-role-${index}`}
                label="Your role / how long"
                value={activity.position || ''}
                onChange={value => updateActivity(index, { position: value })}
              />
              <TextField
                id={`activity-hours-${index}`}
                label="Hours per week"
                value={activity.hours || ''}
                onChange={value => updateActivity(index, { hours: value.replace(/\D/g, '') })}
              />
              <SelectField
                id={`activity-grade-${index}`}
                label="Grade when you did this"
                value={activity.grade || null}
                options={ACTIVITY_GRADES}
                onChange={value => updateActivity(index, { grade: value ?? '' })}
              />
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          onClick={() =>
            setForm(current => ({
              ...current,
              extraCurriculars: [...current.extraCurriculars, { ...EMPTY_ACTIVITY }],
            }))
          }
        >
          + Add activity
        </Button>
      </fieldset>
    </SclaStepCard>
  );
}
